import {
  AppBar,
  Badge,
  Box,
  Button,
  FormControl,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Select,
  Toolbar,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import clsx from "clsx";
import isMobile from "is-mobile";
import { ChevronDown, Crosshair, Menu as MenuIcon, Power, User, Users, Volume2, VolumeX, Wallet } from "lucide-react";
import PopupState, { bindMenu, bindTrigger } from "material-ui-popup-state";
import React, { useCallback, useMemo, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";

import Logo from "@/shared/ui/Logo";
import { lucideSelectIcon } from "@/shared/ui/LucideSelectIcon";
import { getKillLaunchCountdown } from "@/features/kill-initiation";

import { useAppSelector } from "../../app/hooks";
import { RankXPName } from "../../constants/rank";
import useCurrentTime from "../../hook/useCurrentTime";
import useGlobalSound from "../../hook/useGlobalSound";
import { useIsInOwnFamily } from "../../hook/useIsFamilyMember";
import useWallet from "../../hook/useWallet";
import { StateSetter } from "../../types/Common";

import { CHAIN } from "../../config/config";
import configBsc from "../../config/config.bsc";
import configPulseChain from "../../config/config.pulsechain";
import { ChainType, ProfilePlanType } from "../../constants/enum/enum";
import { PERK_TOOLS } from "../../constants/perk";
import { getPerkItem } from "@/features/perks";
import { shortAddress, toUSDFormat } from "../../utils/utils";

import { useStyles } from "./index.styles";
import { designTokens } from "@/shared/styles/tokens";

const HeaderV2 = ({
  toggleMobileSideBar,
  minimal = false,
}: {
  toggleMobileSideBar?: StateSetter<boolean>;
  minimal?: boolean;
}) => {
  const navigate = useNavigate();
  const { classes } = useStyles();
  const theme = useTheme();


  const killLaunch = getKillLaunchCountdown(useCurrentTime() * 1000);
  const { activePerksInfo: activePerks } = useAppSelector(
    (state) => state.perk
  );
  const { connectWallet, account, isConnected, disconnectWallet } = useWallet();
  const { myProfile } = useAppSelector((state) => state.profile);
  const { cashBalance } = useAppSelector((state) => state.bank);
  const { helperCreditBalance } = useAppSelector((state) => state.helperBot);
  const { giCreditBalance } = useAppSelector((state) => state.gameIntelligence);
  const { bulletBalance } = useAppSelector((state) => state.bulletFactory);
  const { healthBalance } = useAppSelector((state) => state.hospital);
  const isInOwnFamily = useIsInOwnFamily();
  const { inboxMessages } = useAppSelector((state) => state.inbox);

  const { citiesTotalPower, citiesTotalDefense } = useAppSelector((state) => state.equipment);

  const [, setOpenChat] = useState(false);

  const onConnectWallet = useCallback(() => {
    setOpenChat(false);
    connectWallet();
  }, [connectWallet]);

  const { muted, toggleMute } = useGlobalSound();

  const rankPercent = Math.floor(myProfile.rankPercent || 0);

  const unreadInboxCount = useMemo(
    () => inboxMessages.filter((message) => message.read_at === null).length,
    [inboxMessages]
  );

  const renderRankInfo = () => {
    return (
      <Box className={classes.menuListBody}>
        <NavLink
          to={`/rank`}
          className={classes.userProfileBtn}
        >
          <Tooltip
            title={
              <>
                {RankXPName[myProfile.rankNumber ? myProfile.rankNumber - 1 : 0] ||
                  ""}
              </>
            }
          >
            <Typography component="span" sx={{ fontWeight: 700 }}>
              {Object.keys(myProfile).length === 0
                ? "No Profile"
                : RankXPName[
                myProfile.rankNumber ? myProfile.rankNumber - 1 : 0
                ]}
            </Typography>
          </Tooltip>

          <Box className={classes.progress} sx={{ position: "relative" }}>
            <LinearProgress
              variant="determinate"
              value={rankPercent}
              className={classes.progressBar}
            />
            <Typography variant="body2" className={classes.progressLabel}>
              {`${rankPercent}%`}
            </Typography>
          </Box>

          <User
            size={18}
            color="currentColor"
            style={{ marginLeft: 8, verticalAlign: "sub" }}
          />
        </NavLink>
      </Box>
    );
  };

  const renderPerkInfo = () => {
    if (myProfile.city === undefined || !account) return <></>;

    // Define your perks data here. Add more types as needed.
    // Add a color property to each perk for custom progress bar color
    const perks = activePerks.activePerks.map((perk: any) => {
      const perkItem = getPerkItem(perk.categoryId, perk.typeId);
      return {
        icon: "/assets/imgs/perks/category/" + perkItem.type + ".png",
        label:
          perkItem.type === "tools"
            ? PERK_TOOLS.find((tool) => tool.id === perkItem.tools)?.label
            : perkItem.type === "success"
              ? "+ " + perkItem.amount + "% " + perkItem.option
              : perkItem.type === "cooldown"
                ? "- " + perkItem.amount + "% " + perkItem.option
                : perkItem.type === "booster"
                  ? "+ " + perkItem.amount + "% " + perkItem.option
                  : perkItem.type === "tools"
                    ? PERK_TOOLS.find((tool) => tool.id === perkItem.tools)?.label
                    : "",
        progress:
          ((Date.now() / 1000 - perk.activationTime) /
            (perk.expirationTime - perk.activationTime)) *
          100,
        duration: perk.duration,
        color:
          perkItem.type === "success"
            ? theme.design.brand.perk.success
            : perkItem.type === "cooldown"
              ? theme.design.brand.perk.cooldown
              : perkItem.type === "booster"
                ? theme.design.brand.perk.booster
                : theme.design.brand.perk.tools,
      };
    });

    return (
      <Tooltip
        title={
          <Box
            sx={{
              backgroundColor: theme.design.color.surface.popover,
              padding: `${theme.design.space.xl}px ${theme.design.space["2xl"]}px`,
              borderRadius: theme.design.radius.xl,
              width: "180px",
              border: `1px solid ${theme.design.color.border.popover}`,
              boxShadow: theme.design.shadow.popover,
              display: "flex",
              flexDirection: "column",
              gap: theme.design.space.sm,
              maxHeight: "200px",
            }}
          >
            {perks.map((perk, idx) => (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexDirection: "column",
                  gap: theme.design.space.xs,
                  borderBottom:
                    idx !== perks.length - 1
                      ? `1px solid ${theme.design.color.border.popover}`
                      : "none",
                  paddingBottom: idx !== perks.length - 1 ? theme.design.space.sm : 0,
                  paddingTop: idx !== 0 ? theme.design.space.sm : 0,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: theme.design.space.sm,
                    width: "100%",
                  }}
                >
                  <Box component={"img"} width={20} src={perk.icon} />
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 700,
                      color: theme.design.color.text.primary,
                      fontFamily: theme.design.typography.fontFamily,
                    }}
                  >
                    {perk.label}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: theme.design.space.xs,
                    width: "100%",
                  }}
                >
                  <LinearProgress
                    variant="determinate"
                    value={perk.progress}
                    sx={{
                      width: "100%",
                      height: "7px",
                      borderRadius: theme.design.radius.sm,
                      backgroundColor: theme.design.color.surface.control,
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: perk.color || theme.design.brand.perk.success,
                      },
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: 700,
                      color: theme.design.color.text.muted,
                      fontFamily: theme.design.typography.fontFamily,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {perk.duration}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        }
        classes={{
          tooltip: classes.customTooltip,
        }}
      >
        <Link to={"/perks"} className={classes.perksLink}>
          <Typography
            textAlign={"center"}
            fontWeight={900}
            color={designTokens.color.text.secondary}
            fontSize={14}
            textTransform={"uppercase"}
          >
            Perks
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              color: theme.design.color.text.primary,
              fontFamily: theme.design.typography.fontFamily,
              fontWeight: 900,
              whiteSpace: "nowrap"
            }}
          >
            <Box
              component={"img"}
              width={20}
              src={"/assets/imgs/perks/lightning.png"}
            ></Box>
            <Typography>
              {activePerks.activePerks.length}/4 active
            </Typography>
          </Box>
        </Link>
      </Tooltip>
    );
  }

  const renderBalances = () => {
    if (!account) return <></>;

    const balanceList = [
      {
        tooltip: "Health History",
        name: "Health",
        balance: healthBalance,
        max: 1000,
        link: `/health/history`,
        icon: "/assets/imgs/inventory/healthitem.png",
      },

      {
        tooltip: "Bullet History",
        name: "Bullets",
        balance: bulletBalance,
        link: `/bullet/history`,
        icon: "/assets/imgs/inventory/bulletitem.png",
      },

      {
        tooltip: "Credit History",
        name: "Credits",
        balance: helperCreditBalance,
        link: `/credit/history`,
        icon: "/assets/imgs/inventory/helperitem.png",
      },
      {
        tooltip: "GI Credit",
        name: "GI Credit",
        balance: giCreditBalance,
        link: `/gicredit/history`,
        icon: "/assets/imgs/inventory/gicredititem.png",
      },
      {
        tooltip: "Cash History",
        name: "Cash",
        balance: cashBalance,
        link: `/cash/history`,
        icon: "/assets/imgs/layout/currency.png",
      },
    ];

    return (
      <Box className={classes.balance}>
        {balanceList.map((balance, index) => (
          <Tooltip title={balance.tooltip} key={index}>

            <Box className={classes.balanceContainer}>
              <Typography
                textAlign={"center"}
                fontWeight={900}
                color={designTokens.color.text.secondary}
                fontSize={14}
                textTransform={"uppercase"}
              >
                {balance.name}
              </Typography>
              <Link to={balance.link} className={classes.currency}>
                <Box component={"img"} width={24} src={balance.icon}></Box>
                {toUSDFormat(Math.floor(balance.balance))}
              </Link>
            </Box>
          </Tooltip>
        ))}
      </Box>
    );
  };

  const renderUserInfo = () => {
    let className = "noPremium";
    if (myProfile.premium === ProfilePlanType.Plus) {
      className = "plus";
    } else if (myProfile.premium === ProfilePlanType.Unlimited) {
      className = "unlimited";
    }

    return (
      <Box className={clsx(classes.profileLinkContainer, className)}>
        {myProfile.premium !== ProfilePlanType.Unlimited && (
          <Typography className={classes.profilePremiumLabel}>Upgrade now!</Typography>
        )}

        <Box
          className={classes.userProfileBtn}
          style={{
            padding: isMobile() ? "7px 12px" : "5px 24px",
          }}
        >
          <Tooltip title={<>{account}</> || ""}>
            <Typography component="span" sx={{ fontWeight: 700, textTransform: "none" }}>
              {Object.keys(myProfile).length === 0
                ? isMobile()
                  ? shortAddress(account, 3, 3)
                  : shortAddress(account)
                : myProfile.name}
            </Typography>
          </Tooltip>

          <User
            size={18}
            color="currentColor"
            style={{ marginLeft: 8, verticalAlign: "sub" }}
          />
        </Box>
      </Box>
    );
  };

  const renderConnectButton = () => {
    return (
      <Tooltip title={isConnected ? "Disconnect" : "Connect"}>
        <Button className={classes.connectWalletBtn} onClick={onConnectWallet}>
          {isMobile() ? (
            <>
              <Wallet size={24} color="currentColor" />
            </>
          ) : (
            <Typography component="span" sx={{ fontWeight: 700 }}>
              Connect Wallet
            </Typography>
          )}
        </Button>
      </Tooltip>
    );
  };

  const renderDefenseOffense = () => {
    if (myProfile.city === undefined || !account) return <></>;
    return <Link to={"/equipment"} className={classes.strengthLink}>
      <Typography
        textAlign={"center"}
        fontWeight={900}
        color={designTokens.color.text.secondary}
        fontSize={14}
        textTransform={"uppercase"}
      >
        Strength
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <img src={"/assets/imgs/equipment/arrow_green.png"} alt="Offense" />
        <Typography>{citiesTotalPower[myProfile.city]}</Typography>
        <Typography>/</Typography>
        <Typography>{citiesTotalDefense[myProfile.city]}</Typography>
        <img src={"/assets/imgs/equipment/arrow_red.png"} alt="Defense" />
      </Box>
    </Link>
  }

  return (
    <Box>
      <AppBar
        position="static"
        sx={{ background: "none", boxShadow: "none", zIndex: 1 }}
      >
        <Toolbar disableGutters className={classes.toolbar}>
          <Box
            className={classes.mdLogoWrapper}
            sx={
              minimal
                ? {
                    display: "flex !important",
                    flexGrow: 1,
                    justifyContent: "flex-start!important",
                  }
                : {
                    flexGrow: { md: 0, lg: 1 },
                    justifyContent: { md: "center", lg: "flex-start" },
                  }
            }
          >
            <Logo
              onClick={() => navigate("/")}
              sx={{
                width: { md: 80, xl: 100 },
                cursor: "pointer",
              }}
            />

            <Box>MAFIA</Box>
          </Box>

          {!minimal && (
            <>
          <Box sx={{ flexGrow: 1, display: { md: "flex", lg: "none" } }}>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={() => toggleMobileSideBar?.(true)}
              color="inherit"
            >
              <MenuIcon />
            </IconButton>
          </Box>

          <FormControl
            className={classes.formControl}
            sx={{
              backgroundColor: "transparent!important",
              border: `1px solid ${theme.design.color.border.default}`,
              marginRight: isMobile() ? "8px!important" : "16px!important",
            }}
          >
            <Select
              value={CHAIN === ChainType.BNB ? "BNB" : "PLS"}
              displayEmpty
              IconComponent={lucideSelectIcon(ChevronDown)}
              inputProps={{
                MenuProps: {
                  PaperProps: {
                    style: {
                      maxHeight: 320,
                    },
                    sx: {
                      display: "block",
                      padding: 0,
                      backgroundColor: theme.design.color.surface.interactive,
                      color: theme.design.color.text.primary,
                    },
                  },
                  children: {
                    sx: {
                      fontSize: "12px",
                    },
                  },
                },
              }}
              onChange={(item) => {
                if (item.target.value === "BNB") {
                  window.location.href = `https://${configBsc.domain}`
                } else if (item.target.value === "PLS") {
                  window.location.href = `https://${configPulseChain.domain}`
                } else if (item.target.value === "MONAD") {
                  window.location.href = "https://monadmafia.io";
                } else if (item.target.value === "BASE") {
                  window.location.href = "https://basemafia.io";
                }
              }}
            >
              {["BNB", "PLS", "MONAD", "BASE"].map((item, index) => (
                <MenuItem
                  key={index}
                  value={item.toString()}
                  disabled={item === "MONAD" || item === "BASE"}
                >
                  <Box
                    sx={{
                      fontSize: "12px",
                      fontWeight: 700,
                      color: theme.design.color.text.primary,
                      fontFamily: theme.design.typography.fontFamily,
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <Box
                      component={"img"}
                      src={`https://play-mafia.sfo3.cdn.digitaloceanspaces.com/3rd-brandings/chain-logos/${item.toLowerCase()}.png`}
                      sx={{
                        width: 24,
                        height: 24,
                      }}
                    />
                    {!isMobile() ? item : ""}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {renderDefenseOffense()}
          {renderPerkInfo()}
          {renderBalances()}

          {isConnected && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                marginRight: isMobile() ? 1 : 2,
              }}
            >
              <Badge badgeContent={unreadInboxCount} color="error">
                <Button
                  className={classes.inboxButton}
                  onClick={() => navigate("/inbox")}
                >
                  <Box
                    component={"img"}
                    width={32}
                    height={32}
                    src={"/assets/imgs/inbox/open-email.png"}
                  ></Box>
                </Button>
              </Badge>
            </Box>
          )}

          <Box
            component={Link}
            to="/kill-initiation"
            className={classes.killLaunchLink}
            aria-label={`Kill initiation: ${killLaunch.full}`}
          >
            <Box className={classes.killLaunchIcon} aria-hidden="true">
              <Crosshair size={16} strokeWidth={1.8} />
            </Box>
            <Box className={classes.killLaunchCopy}>
              <Typography className={classes.killLaunchLabel}>
                {killLaunch.state === "counting" ? "KILL INITIATION IN" : "KILL INITIATION"}
              </Typography>
              <Typography className={classes.killLaunchTime}>{killLaunch.compact}</Typography>
            </Box>
          </Box>
            </>
          )}

          <Box className={classes.userSection}>
            {isConnected ?
              renderRankInfo()
              :
              renderConnectButton()
            }

            {isConnected && <Button className={classes.headerActionButton}>
              <PopupState variant="popover" popupId="demo-popup-menu">
                {(popupState) => (
                  <React.Fragment>
                    <Box
                      {...bindTrigger(popupState)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >

                      {renderUserInfo()}
                    </Box>

                    <Menu
                      {...bindMenu(popupState)}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "center",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "center",
                      }}
                      sx={{
                        "& .MuiPaper-root": {
                          backgroundColor: theme.design.color.surface.popover,
                          minWidth: "120px",
                        },
                        "& .MuiMenuItem-gutters": {
                          minHeight: "48px",
                        },
                        mt: 1.5,
                      }}
                    >
                      {isInOwnFamily && (
                        <MenuItem
                          onClick={() => {
                            popupState.close();
                          }}
                        >
                          <Tooltip title="Go to Family" placement="left">
                            <NavLink
                              to={`family/${myProfile.userFamily?.name}`}
                              className={classes.profileLinkText}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: "8px",
                                  color: theme.design.color.text.primary,
                                  alignItems: "center",
                                  fontFamily: theme.design.typography.fontFamily,
                                  fontWeight: 700,
                                }}
                              >
                                <Users size={21} color="currentColor" />
                                My family
                              </Box>
                            </NavLink>
                          </Tooltip>
                        </MenuItem>
                      )}

                      {myProfile && myProfile.name && (
                        <MenuItem onClick={popupState.close}>
                          <Tooltip title="Go to Profile" placement="left">
                            <Box>
                              <NavLink
                                to={`profile/${myProfile?.name}`}
                                style={{
                                  fontFamily: theme.design.typography.fontFamily,
                                  fontWeight: 700,
                                  textDecoration: "none",
                                  color: theme.design.color.text.primary,
                                  display: "flex",
                                  gap: "8px",
                                  alignItems: "center",
                                }}
                              >
                                <User size={21} color="currentColor" />
                                My Profile
                              </NavLink>
                            </Box>
                          </Tooltip>
                        </MenuItem>
                      )}

                      <MenuItem
                        onClick={() => {
                          popupState.close();
                          toggleMute();
                        }}
                      >
                        <Tooltip title="Mute Options" placement="left">
                          <Box
                            sx={{
                              display: "flex",
                              gap: "8px",
                              color: theme.design.color.text.primary,
                              alignItems: "center",
                              fontFamily: theme.design.typography.fontFamily,
                              fontWeight: 700,
                            }}
                          >
                            {!muted ? (
                              <VolumeX size={21} color="currentColor" />
                            ) : (
                              <Volume2 size={21} color="currentColor" />
                            )}
                            {!muted ? "Mute" : "Unmute"}
                          </Box>
                        </Tooltip>
                      </MenuItem>

                      <MenuItem
                        onClick={() => {
                          popupState.close();
                          isConnected ? disconnectWallet() : onConnectWallet();
                        }}
                      >
                        <Tooltip
                          title={isConnected ? "Disconnect" : "Connect"}
                          placement="left"
                        >
                          <Box
                            sx={{
                              display: "flex",
                              gap: "8px",
                              alignItems: "center",
                              fontFamily: theme.design.typography.fontFamily,
                              fontWeight: 700,
                            }}
                          >
                            <Power size={24} color="currentColor" />
                            {isConnected ? "Disconnect" : "Connect"}
                          </Box>
                        </Tooltip>
                      </MenuItem>
                    </Menu>
                  </React.Fragment>
                )}
              </PopupState>
            </Button>}
          </Box>
        </Toolbar>
      </AppBar>
    </Box>
  );
};

export default HeaderV2;
